import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";

const SESSION_KEY = "djval_seen_sections";

function getSeen(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function markSeen(name: string): void {
  if (typeof window === "undefined") return;
  const seen = getSeen();
  seen.add(name);
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    /* ignore */
  }
}

/**
 * Attache un IntersectionObserver a l'element retourne.
 * Envoie `section_view` { section_name } une seule fois par session et par section.
 */
export function useSectionView<T extends HTMLElement = HTMLElement>(
  sectionName: string,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = ref.current;
    if (!el) return;
    if (getSeen().has(sectionName)) return;
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            if (!getSeen().has(sectionName)) {
              markSeen(sectionName);
              trackEvent("section_view", { section_name: sectionName });
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: [0.5] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sectionName]);
  return ref;
}
